import { useEffect, useRef, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { toast } from "sonner";
import { ArrowUp, ArrowLeft, Feather } from "lucide-react";
import { api } from "@/lib/api";
import { Orb } from "@/components/Orb";

export default function Interview() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [interview, setInterview] = useState(null);
  const [loading, setLoading] = useState(true);
  const [answer, setAnswer] = useState("");
  const [sending, setSending] = useState(false);
  const bottomRef = useRef(null);
  const taRef = useRef(null);

  const load = async () => {
    try {
      const res = await api.get(`/interviews/${id}`);
      setInterview(res.data);
    } catch (e) {
      toast.error("Couldn't open this conversation.");
      navigate("/dashboard", { replace: true });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [id]);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [interview?.messages?.length, sending]);

  const send = async () => {
    const text = answer.trim();
    if (!text || sending) return;
    setSending(true);
    // optimistic user message
    setInterview((prev) => ({
      ...prev,
      messages: [...prev.messages, { role: "user", text, kind: "user", optimistic: true }],
    }));
    setAnswer("");
    if (taRef.current) taRef.current.style.height = "auto";
    try {
      const res = await api.post(`/interviews/${id}/respond`, { answer: text });
      setInterview(res.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "The guide couldn't respond. Please try again.");
      // rollback optimistic
      setInterview((prev) => ({
        ...prev,
        messages: prev.messages.filter((m) => !m.optimistic),
      }));
      setAnswer(text);
    } finally {
      setSending(false);
    }
  };

  const onKeyDown = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      send();
    }
  };

  const autoGrow = (e) => {
    setAnswer(e.target.value);
    e.target.style.height = "auto";
    e.target.style.height = Math.min(e.target.scrollHeight, 180) + "px";
  };

  if (loading || !interview) {
    return (
      <div className="bf-noise flex min-h-screen items-center justify-center">
        <Orb stage={4} total={8} />
        <div className="h-10 w-10 animate-spin rounded-full border-2 border-bf-gold/30 border-t-bf-gold" />
      </div>
    );
  }

  const completed = interview.status === "completed";
  const stage = completed ? interview.total_questions : interview.current_stage;

  return (
    <div className="bf-noise relative min-h-screen overflow-hidden">
      <Orb stage={stage} total={interview.total_questions} size={520} />

      <header className="relative z-20 mx-auto flex w-full max-w-2xl items-center justify-between px-6 py-5">
        <button
          data-testid="back-to-dashboard-button"
          onClick={() => navigate("/dashboard")}
          className="inline-flex items-center gap-1.5 font-sans text-xs text-bf-muted transition-colors duration-300 hover:text-bf-text"
        >
          <ArrowLeft className="h-4 w-4" /> Dashboard
        </button>
        <div className="flex items-center gap-2 font-sans text-xs uppercase tracking-[0.18em] text-bf-muted/70">
          <Feather className="h-3.5 w-3.5 text-bf-gold" />
          {completed ? "Reflection" : `${Math.min(stage + 1, interview.total_questions)} of ${interview.total_questions}`}
        </div>
      </header>

      <main className={`relative z-10 mx-auto w-full max-w-2xl px-6 pt-4 ${completed ? "pb-20" : "pb-44"}`}>
        <div className="space-y-10" data-testid="conversation-thread">
          <AnimatePresence initial={false}>
            {interview.messages.map((m, i) => (
              <Message key={i} m={m} />
            ))}
          </AnimatePresence>

          {sending && <Typing />}

          {completed && <CompletedFooter navigate={navigate} />}
          <div ref={bottomRef} />
        </div>
      </main>

      {!completed && (
        <div className="fixed bottom-0 left-0 right-0 z-20 border-t border-white/10 bg-bf-bg2/70 backdrop-blur-xl">
          <div className="mx-auto flex w-full max-w-2xl items-end gap-3 px-6 py-4">
            <textarea
              ref={taRef}
              data-testid="answer-input"
              rows={1}
              value={answer}
              onChange={autoGrow}
              onKeyDown={onKeyDown}
              placeholder="Take your time…"
              className="bf-scroll max-h-[180px] flex-1 resize-none rounded-2xl border border-white/12 bg-white/[0.04] px-4 py-3 font-serif text-lg leading-relaxed text-bf-text placeholder:text-bf-muted/40 focus:border-bf-gold/50 focus:outline-none"
            />
            <button
              data-testid="send-answer-button"
              onClick={send}
              disabled={sending || !answer.trim()}
              className="flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-full bg-gradient-to-r from-bf-gold to-bf-rose text-[#2A1521] shadow-[0_0_28px_-8px_rgba(255,180,90,0.7)] transition-transform duration-300 hover:scale-105 disabled:opacity-40"
            >
              <ArrowUp className="h-5 w-5" />
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

const Message = ({ m }) => {
  if (m.role === "user") {
    return (
      <motion.div
        initial={{ opacity: 0, y: 16 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        className="flex justify-end"
      >
        <p
          data-testid="user-message"
          className="max-w-[85%] rounded-2xl rounded-br-md border border-white/10 bg-white/[0.05] px-5 py-3 font-sans text-base leading-relaxed text-bf-text/90"
        >
          {m.text}
        </p>
      </motion.div>
    );
  }

  const isReflection = m.kind === "reflection";
  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.6, ease: "easeOut" }}
      className="max-w-[92%]"
    >
      {m.ack ? (
        <p className="mb-3 font-sans text-sm leading-relaxed text-bf-muted">{m.ack}</p>
      ) : null}
      {isReflection ? (
        <div
          data-testid="final-reflection"
          className="rounded-2xl border border-bf-gold/25 bg-gradient-to-b from-bf-gold/[0.07] to-transparent p-6"
        >
          <p className="mb-4 font-sans text-xs uppercase tracking-[0.18em] text-bf-gold/80">
            A reflection for you
          </p>
          <div className="space-y-4">
            {m.text.split(/\n\n+/).map((para, idx) => (
              <p key={idx} className="font-serif text-xl leading-relaxed text-bf-text">
                {para}
              </p>
            ))}
          </div>
        </div>
      ) : (
        <p
          data-testid="guide-question"
          className="font-serif text-2xl leading-relaxed text-bf-text sm:text-[1.7rem]"
        >
          {m.text}
        </p>
      )}
    </motion.div>
  );
};

const Typing = () => (
  <motion.div
    initial={{ opacity: 0 }}
    animate={{ opacity: 1 }}
    className="flex items-center gap-1.5 pl-1"
    data-testid="guide-typing"
  >
    {[0, 1, 2].map((i) => (
      <motion.span
        key={i}
        className="h-2 w-2 rounded-full bg-bf-gold/70"
        animate={{ opacity: [0.3, 1, 0.3], y: [0, -3, 0] }}
        transition={{ duration: 1.1, repeat: Infinity, delay: i * 0.18 }}
      />
    ))}
  </motion.div>
);

const CompletedFooter = ({ navigate }) => (
  <motion.div
    initial={{ opacity: 0, y: 16 }}
    animate={{ opacity: 1, y: 0 }}
    transition={{ duration: 0.6, delay: 0.2 }}
    className="pt-4"
  >
    <button
      data-testid="finish-to-dashboard-button"
      onClick={() => navigate("/dashboard")}
      className="inline-flex items-center gap-2 rounded-full border border-white/20 px-6 py-3 font-sans text-sm text-bf-text transition-colors duration-300 hover:border-bf-gold/50"
    >
      This is saved for you — return anytime
    </button>
  </motion.div>
);
